/**
 * The step indicator of a multi-step form: where the user is, what came
 * before, what is still to come.
 *
 * Every step is a button, so the flow is a map rather than a corridor — a
 * user editing an existing dataset jumps straight to the step holding the
 * one field they came to change instead of walking there through Next. The
 * component decides nothing about whether a jump is allowed: `onSelect`
 * receives the request and the form, which owns the validation, decides.
 *
 * `aria-current="step"` on the active item is the platform's own vocabulary
 * for this ("step 3 of 5, current" to a screen reader). It is deliberately
 * not a `tablist`: tabs promise that every panel is present and switchable at
 * will, and a wizard whose later steps validate the earlier ones does not
 * keep that promise.
 */

import { Check } from 'lucide-react';
import { cn } from '@lean-agility/design-system/react';

export interface StepNavStep {
  id: string;
  /** Short: one or two words. The step's own heading carries the sentence. */
  label: string;
}

export function StepNav({
  steps,
  current,
  onSelect,
  disabled,
  label = 'Steps',
}: {
  steps: readonly StepNavStep[];
  current: string;
  onSelect: (id: string) => void;
  disabled?: boolean;
  /** The nav landmark's name. */
  label?: string;
}) {
  const currentIndex = steps.findIndex((s) => s.id === current);

  return (
    <nav aria-label={label}>
      <ol className="flex flex-wrap gap-x-1 gap-y-2">
        {steps.map((step, index) => {
          const isCurrent = index === currentIndex;
          const isDone = index < currentIndex;
          return (
            <li key={step.id} className="flex items-center gap-1">
              {index > 0 && <span aria-hidden="true" className="h-px w-3 bg-border-default" />}
              <button
                type="button"
                onClick={() => onSelect(step.id)}
                disabled={disabled}
                aria-current={isCurrent ? 'step' : undefined}
                className={cn(
                  `
                    flex items-center gap-1.5 rounded-sm px-2 py-1 text-sm text-text-muted
                    hover:text-text-primary
                    focus:ring-1 focus:ring-primary-500 focus:outline-none
                    disabled:opacity-50
                  `,
                  // `cn` merges: the current step's colour wins over the muted base.
                  isCurrent && 'font-medium text-text-primary',
                )}
              >
                <span
                  aria-hidden="true"
                  className={cn(
                    'flex size-5 shrink-0 items-center justify-center rounded-full text-xs',
                    isCurrent && 'bg-action-primary text-white',
                    isDone && 'bg-feedback-success/15 text-feedback-success',
                    !isCurrent && !isDone && 'bg-bg-muted text-text-muted',
                  )}
                >
                  {isDone ? <Check className="size-3" /> : index + 1}
                </span>
                {step.label}
              </button>
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
